import { sortedDeepEntries } from "./object";

const FALLBACK_LOCALE = "en-US";

/**
 * Build a formatter in the requested locale, falling back to `FALLBACK_LOCALE`
 * when the runtime rejects it. Shared by the date and currency factories so the
 * two cannot drift apart again — they previously carried the same guard but
 * degraded differently, one throwing and one silently dropping the currency
 * symbol.
 */
function buildFormatter<T>(build: () => T, fallback: () => T): T {
  try {
    return build();
  } catch {
    return fallback();
  }
}

// FORMAT DATE
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

export function formatDate(
  date: Date,
  locale: Intl.LocalesArgument | null = "en-US",
  formatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error(`Invalid date value: ${date}`);
  }

  const resolvedLocale = (locale ?? "en-US") as Intl.LocalesArgument & string;

  const formatter = getDateFormatter(resolvedLocale, formatOptions);

  return formatter.format(date);
}

function getDateFormatter(
  locale: Intl.LocalesArgument & string,
  formatOptions: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}-${JSON.stringify(sortedDeepEntries(formatOptions as Record<string, unknown>, 1))}`;

  const existing = dateFormatters.get(key);
  if (existing) return existing;

  // `supportedLocalesOf` returning `[]` does not mean the constructor will
  // throw, so it is no gate to put in front of one: such a check, not the
  // constructor, is what red-boxes a Chinese customer. Measured on
  // Hermes/Android: `zh-CN` constructs fine and quietly resolves to en-US
  // output, while `es-US` and `ko-KR` resolve natively (`18 jul 2026`,
  // `2026년 7월 18일`). So an unknown-but-well-formed tag degrades on its own
  // and never reaches the catch. What still throws is a malformed tag —
  // `en_US` in underscore form, or an empty string — which is exactly what the
  // fallback is here to absorb; keep it. Native Chinese dates would need an
  // Intl polyfill, which this app does not ship.
  const formatter = buildFormatter(
    () => new Intl.DateTimeFormat(locale, formatOptions),
    () => new Intl.DateTimeFormat(FALLBACK_LOCALE, formatOptions),
  );

  dateFormatters.set(key, formatter);
  return formatter;
}

// FORMAT CURRENCY
export function formatCurrency(
  value: number,
  locale: Intl.LocalesArgument | null = "en-US",
  currency: string | null = "USD",
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    throw new Error(`Invalid number value: ${value}`);
  }

  const resolvedLocale = (locale ?? "en-US") as Intl.LocalesArgument & string;
  const resolvedCurrency = currency ?? "USD";

  try {
    const formatter = getCurrencyFormatter(
      resolvedLocale,
      resolvedCurrency,
      minimumFractionDigits,
      maximumFractionDigits,
    );
    return formatter.format(value);
  } catch (error) {
    console.error(error);
    return value.toString();
  }
}

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function getCurrencyFormatter(
  locale: Intl.LocalesArgument & string,
  currency: string,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
): Intl.NumberFormat {
  const key = `${locale}-${currency}-${minimumFractionDigits}-${maximumFractionDigits}`;

  const existing = currencyFormatters.get(key);
  if (existing) return existing;

  if (!Intl.supportedValuesOf("currency").includes(currency)) {
    throw new Error(`Unsupported currency: ${currency}`);
  }

  const numberFormatOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  };

  const formatter = buildFormatter(
    () => new Intl.NumberFormat(locale, numberFormatOptions),
    () => new Intl.NumberFormat(FALLBACK_LOCALE, numberFormatOptions),
  );

  currencyFormatters.set(key, formatter);
  return formatter;
}

export function stripNonDigits(value: string): string {
  let result = "";
  for (const char of value) {
    if (char >= "0" && char <= "9") {
      result += char;
    }
  }
  return result;
}

export function isAllAsciiDigits(s: string): boolean {
  return stripNonDigits(s).length === s.length;
}

export function formatUsZipPlus4(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}
